/**
 * PaneelM2Calculator
 *
 * Modes (via data-calc-mode on [data-paneel-m2-calculator]):
 *   panel — qty = hele panelen; m2 = qty × m2PerUnit
 *   epdm  — qty = strekkende meters (ceil, max data-qty-max); m2 = qty × m2PerUnit
 *
 *   <div
 *     data-paneel-m2-calculator
 *     data-calc-mode="panel|epdm"
 *     data-m2-per-unit="{{ ... }}"
 *     data-price-cents="{{ ... }}"
 *     data-currency="{{ cart.currency.iso_code }}"
 *     data-qty-input-id="quantity-{{ calc_uid }}"
 *     data-qty-max="30"
 *   >
 *     <input data-calc-qty>
 *     <input data-calc-m2>
 *     <div data-calc-result></div>
 *     <div data-calc-total></div>
 *   </div>
 */
class PaneelM2Calculator {
   /**
    * @param {HTMLElement} root
    */
   constructor(root) {
      this.root = root;

      this.mode = root.getAttribute('data-calc-mode') || 'panel';
      this.m2PerUnit =
         parseFloat(root.getAttribute('data-m2-per-unit')) ||
         parseFloat(root.getAttribute('data-m2-per-paneel')) ||
         0;
      this.priceCents = parseInt(root.getAttribute('data-price-cents'), 10) || 0;
      this.currency = root.getAttribute('data-currency') || 'EUR';
      this.qtyMax = root.hasAttribute('data-qty-max')
         ? parseInt(root.getAttribute('data-qty-max'), 10)
         : null;
      this.qtyInputId =
         root.getAttribute('data-qty-input-id') || `quantity-${root.getAttribute('data-calc-uid') || ''}`;

      this.qtyInput =
         root.querySelector('[data-calc-qty]') || root.querySelector('[data-calc-panelen]');
      this.m2Input = root.querySelector('[data-calc-m2]');
      this.resultEl = root.querySelector('[data-calc-result]');
      this.totalEl = root.querySelector('[data-calc-total]');

      this.syncingQty = false;

      try {
         this.moneyFmt = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: this.currency });
      } catch (e) {
         this.moneyFmt = null;
      }

      this._onQtyInput = this._onQtyInput.bind(this);
      this._onM2Input = this._onM2Input.bind(this);
      this._onM2Blur = this._onM2Blur.bind(this);
      this._onDocumentChange = this._onDocumentChange.bind(this);
      this._onVariantUpdated = this._onVariantUpdated.bind(this);
      this._onTaxToggleChange = this._onTaxToggleChange.bind(this);

      this._bindEvents();
      this._scheduleToggleTaxRepair();
      this.applyQty(1);
   }

   // ---------------------------------------------------------------
   // Setup
   // ---------------------------------------------------------------

   _bindEvents() {
      this.qtyInput.addEventListener('input', this._onQtyInput);

      // Live m2 → qty, maar m2Input.value niet overschrijven tijdens typen.
      this.m2Input.addEventListener('input', this._onM2Input);
      this.m2Input.addEventListener('blur', this._onM2Blur);

      document.addEventListener('change', this._onDocumentChange);
      document.addEventListener('product-variant-updated', this._onVariantUpdated);
      document.addEventListener('change', this._onTaxToggleChange);
   }

   // ---------------------------------------------------------------
   // Event handlers
   // ---------------------------------------------------------------

   _onQtyInput() {
      const raw = this._parseDecimal(this.qtyInput.value);
      this.applyQty(raw);
   }

   _onM2Input() {
      const m2 = this._parseDecimal(this.m2Input.value);
      if (isNaN(m2) || m2 <= 0) {
         return;
      }
      if (this.m2PerUnit > 0) {
         const qty = Math.ceil(m2 / this.m2PerUnit);
         this.applyQty(qty, { skipM2Field: true });
      }
   }

   _onM2Blur() {
      const qty = parseInt(this.qtyInput.value, 10) || 1;
      this.m2Input.value = this._formatM2(qty * this.m2PerUnit);
   }

   _onDocumentChange(e) {
      if (this.syncingQty) {
         return;
      }
      if (!e.target || e.target.id !== this.qtyInputId) {
         return;
      }
      let qty = parseInt(e.target.value, 10);
      if (isNaN(qty) || qty < 1) {
         qty = 1;
      }
      this.applyQty(qty);
   }

   _onVariantUpdated(e) {
      if (e.detail && e.detail.selected && typeof e.detail.selected.price !== 'undefined') {
         this.priceCents = e.detail.selected.price;
         const currentQty = parseInt(this.qtyInput.value, 10) || 1;
         this.applyQty(currentQty, { skipM2Field: true });
      }
   }

   _onTaxToggleChange(e) {
      if (!e.target || !e.target.classList.contains('tax-toggle-input')) {
         return;
      }
      if (this._repairingTax) {
         return;
      }
      this._taxStateOverride = e.target.checked ? 'included' : 'excluded';
      const currentQty = parseInt(this.qtyInput.value, 10) || 1;
      this.applyQty(currentQty, { skipM2Field: true });
      this._taxStateOverride = null;
   }

   /**
    * ToggleTax back-computes native wrong on full page load when catalog
    * prices are excl. and saved state is "included": it treats the fresh
    * Shopify price as already-incl and divides by (1+rate). Product title
    * then stays at excl while this calculator correctly shows incl.
    * Re-capture natives via excl → incl so both stay in sync.
    */
   _scheduleToggleTaxRepair() {
      const attempt = () => {
         if (this._repairToggleTaxNativeCapture()) {
            const qty = parseInt(this.qtyInput.value, 10) || 1;
            this.applyQty(qty, { skipM2Field: true });
         }
      };
      attempt();
      setTimeout(attempt, 0);
      setTimeout(attempt, 150);
   }

   /**
    * @returns {boolean} true when a repair ran
    */
   _repairToggleTaxNativeCapture() {
      const cfg = this._getTaxConfig();
      if (!cfg || cfg.baseIncludesTax) {
         return false;
      }
      if (this._getTaxState() !== 'included') {
         return false;
      }

      const priceEl =
         document.querySelector('.product-price .price-item--regular') ||
         document.querySelector('.product-price .price-item');
      if (!priceEl) {
         return false;
      }

      const catalog = this.priceCents / 100;
      if (!catalog) {
         return false;
      }

      const multiplier = 1 + cfg.taxRate / 100;
      const displayed = this._parseMoneyValue(priceEl.textContent);
      if (displayed === null) {
         return false;
      }

      // Already showing incl. for this variant
      if (Math.abs(displayed - catalog * multiplier) < 0.05) {
         return false;
      }

      // Stuck on excl. catalog while toggle says incl.
      if (Math.abs(displayed - catalog) > 0.05) {
         return false;
      }

      document.querySelectorAll('[data-tax-native]').forEach((el) => {
         el.removeAttribute('data-tax-native');
         el.removeAttribute('data-tax-meta');
      });

      const checkbox = document.querySelector('.tax-toggle-input');
      if (!checkbox) {
         return false;
      }

      this._repairingTax = true;
      try {
         if (checkbox.checked) {
            checkbox.click();
         }
         if (!checkbox.checked) {
            checkbox.click();
         }
      } finally {
         this._repairingTax = false;
      }

      return true;
   }

   /**
    * @param {string} str
    * @returns {number|null}
    */
   _parseMoneyValue(str) {
      if (!str) {
         return null;
      }
      const cleaned = String(str).replace(/\u00a0/g, ' ').trim();
      const match = cleaned.match(/[\d.,]+/);
      if (!match) {
         return null;
      }
      let raw = match[0];
      const lastComma = raw.lastIndexOf(',');
      const lastDot = raw.lastIndexOf('.');
      if (lastComma > lastDot) {
         raw = raw.replace(/\./g, '').replace(',', '.');
      } else {
         raw = raw.replace(/,/g, '');
      }
      const value = parseFloat(raw);
      return isNaN(value) ? null : value;
   }

   // ---------------------------------------------------------------
   // Core
   // ---------------------------------------------------------------

   /**
    * @param {number} qty
    * @param {{skipM2Field?: boolean}} [opts]
    * @returns {number}
    */
   applyQty(qty, opts = {}) {
      const cartQtyInput = this._getQtyInput();

      if (!qty || qty < 1 || isNaN(qty)) {
         qty = 1;
      }
      qty = Math.ceil(qty);
      qty = this._clampQty(qty, cartQtyInput);

      const actualM2 = qty * this.m2PerUnit;

      this.qtyInput.value = qty;
      if (!opts.skipM2Field) {
         this.m2Input.value = this._formatM2(actualM2);
      }

      if (this.resultEl) {
         this.resultEl.textContent = this._resultText(qty, actualM2);
      }

      if (this.totalEl) {
         const totalEuros = this._displayPriceFromNativeCents(qty * this.priceCents);
         this.totalEl.textContent = 'Totaal: ' + this._formatMoney(totalEuros);
      }

      if (cartQtyInput) {
         if (this.qtyMax !== null && !isNaN(this.qtyMax)) {
            cartQtyInput.setAttribute('max', String(this.qtyMax));
         }
         this.syncingQty = true;
         this._setNativeInputValue(cartQtyInput, String(qty));
         this._dispatchInputAndChange(cartQtyInput);
         this.syncingQty = false;
      }

      return qty;
   }

   /** @deprecated Use applyQty */
   applyPanelen(panelen, opts = {}) {
      return this.applyQty(panelen, opts);
   }

   destroy() {
      this.qtyInput.removeEventListener('input', this._onQtyInput);
      this.m2Input.removeEventListener('input', this._onM2Input);
      this.m2Input.removeEventListener('blur', this._onM2Blur);
      document.removeEventListener('change', this._onDocumentChange);
      document.removeEventListener('product-variant-updated', this._onVariantUpdated);
      document.removeEventListener('change', this._onTaxToggleChange);
      this.root.removeAttribute('data-calc-initialized');
   }

   // ---------------------------------------------------------------
   // Helpers
   // ---------------------------------------------------------------

   /**
    * @returns {HTMLInputElement|null}
    */
   _getQtyInput() {
      const el = document.getElementById(this.qtyInputId);
      if (el) {
         return el;
      }
      const summaryInner = this.root.closest('.product-summary-inner') || document;
      const form = summaryInner.querySelector('[data-type="add-to-cart-form"]');
      return form ? form.querySelector('.quantity-input') : null;
   }

   _setNativeInputValue(el, value) {
      const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      nativeSetter.call(el, value);
   }

   _dispatchInputAndChange(el) {
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
   }

   _formatMoney(amountEuros) {
      if (this.moneyFmt) {
         return this.moneyFmt.format(amountEuros);
      }
      return '€ ' + amountEuros.toFixed(2).replace('.', ',');
   }

   /**
    * Tax config mirrors ToggleTax: catalog prices are excl. BTW when the
    * widget has data-base-includes-tax="false" (theme setting
    * base_price_includes_tax). Fall back to the calculator root attribute
    * (from cart.taxes_included) only when the widget is absent.
    * @returns {{taxRate: number, baseIncludesTax: boolean}|null}
    */
   _getTaxConfig() {
      const attr = this.root.getAttribute('data-base-includes-tax');
      const hasShopifyFlag = attr === 'true' || attr === 'false';

      const widget = document.querySelector(
         '.toggle-tax-widget:not(.toggle-tax-widget--cart-injected)'
      );

      let taxRate = 21;
      if (widget) {
         const parsed = parseFloat(widget.dataset.taxRate);
         if (!isNaN(parsed)) {
            taxRate = parsed;
         }
      }

      if (!widget && !hasShopifyFlag) {
         return null;
      }

      // Prefer ToggleTax: it owns the visible product price conversion.
      return {
         taxRate,
         baseIncludesTax: widget
            ? widget.dataset.baseIncludesTax === 'true'
            : attr === 'true',
      };
   }

   /**
    * @returns {'included'|'excluded'}
    */
   _getTaxState() {
      if (this._taxStateOverride === 'included' || this._taxStateOverride === 'excluded') {
         return this._taxStateOverride;
      }
      try {
         const stored = localStorage.getItem('toggle_tax_state');
         if (stored === 'included' || stored === 'excluded') {
            return stored;
         }
      } catch (e) {
         // ignore
      }
      const checkbox = document.querySelector('.tax-toggle-input');
      if (checkbox) {
         return checkbox.checked ? 'included' : 'excluded';
      }
      return 'excluded';
   }

   /**
    * @param {number} nativeCents
    * @returns {number}
    */
   _displayPriceFromNativeCents(nativeCents) {
      const cfg = this._getTaxConfig();
      let euros = nativeCents / 100;
      if (!cfg) {
         return euros;
      }

      const multiplier = 1 + cfg.taxRate / 100;
      const state = this._getTaxState();

      if (cfg.baseIncludesTax && state === 'excluded') {
         euros = euros / multiplier;
      } else if (!cfg.baseIncludesTax && state === 'included') {
         euros = euros * multiplier;
      }

      return euros;
   }

   _formatM2(value) {
      return value.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
   }

   _parseDecimal(str) {
      if (!str) {
         return NaN;
      }
      return parseFloat(String(str).replace(',', '.'));
   }

   /**
    * @param {number} qty
    * @param {HTMLInputElement|null} cartQtyInput
    * @returns {number}
    */
   _clampQty(qty, cartQtyInput) {
      let min = 1;
      let max = this.qtyMax !== null && !isNaN(this.qtyMax) ? this.qtyMax : null;

      if (cartQtyInput) {
         const inputMin = parseInt(cartQtyInput.getAttribute('min'), 10);
         if (!isNaN(inputMin)) {
            min = inputMin;
         }
         if (cartQtyInput.hasAttribute('max')) {
            const inputMax = parseInt(cartQtyInput.getAttribute('max'), 10);
            if (!isNaN(inputMax)) {
               max = max === null ? inputMax : Math.min(max, inputMax);
            }
         }
      }

      if (qty < min) {
         qty = min;
      }
      if (max !== null && qty > max) {
         qty = max;
      }
      return qty;
   }

   /**
    * @param {number} qty
    * @param {number} actualM2
    * @returns {string}
    */
   _resultText(qty, actualM2) {
      const m2 = this._formatM2(actualM2);
      if (this.mode === 'epdm') {
         return (
            'Gecommitteerd: ' +
            qty +
            ' strekkende meter' +
            (qty === 1 ? '' : 's') +
            ' = ' +
            m2 +
            ' m2'
         );
      }
      return (
         'Gecommitteerd: ' +
         qty +
         ' paneel' +
         (qty === 1 ? '' : 'en') +
         ' = ' +
         m2 +
         ' m2'
      );
   }

   // ---------------------------------------------------------------
   // Static init
   // ---------------------------------------------------------------

   /**
    * @param {HTMLElement} root
    * @returns {PaneelM2Calculator|null}
    */
   static init(root) {
      if (!root || root.getAttribute('data-calc-initialized') === '1') {
         return null;
      }
      root.setAttribute('data-calc-initialized', '1');
      return new PaneelM2Calculator(root);
   }

   /**
    * @param {ParentNode} [context=document]
    * @returns {PaneelM2Calculator[]}
    */
   static initAll(context = document) {
      const roots = context.querySelectorAll('[data-paneel-m2-calculator]');
      const instances = [];
      roots.forEach((root) => {
         const instance = PaneelM2Calculator.init(root);
         if (instance) {
            instances.push(instance);
         }
      });
      return instances;
   }
}

if (document.readyState === 'loading') {
   document.addEventListener('DOMContentLoaded', () => PaneelM2Calculator.initAll());
} else {
   PaneelM2Calculator.initAll();
}

document.addEventListener('shopify:section:load', (e) => {
   PaneelM2Calculator.initAll(e.target);
});
